import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ZeplinTitle,
  ZeplinDataSubtitle,
  ZeplinStepIndicator,
  ZeplinFieldGroup,
  ZeplinLabel,
  ZeplinSelect,
  ZeplinButton,
  ZeplinInput,
  ZeplinTileGroup,
  ZeplinTile,
  ZeplinValidCheck,
  ZeplinError,
  ZeplinFieldsReveal
} from '../../styles/ZeplinStyles';
import { TIPOS_CONTATO } from '../../constants/tiposContato';
import { validatePhone } from '../../utils/validators';

// Funções de formatação
const formatCPF = (value: string) => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 11) {
    return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return cleanValue.slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatCNPJ = (value: string) => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 14) {
    return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return cleanValue.slice(0, 14).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatPhone = (value: string) => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 11) {
    if (cleanValue.length <= 10) {
      return cleanValue.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return cleanValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return cleanValue.slice(0, 11).replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

// Regex simples de e-mail para validação positiva
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Ícone de usuário (pessoa física)
const UserIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Ícone de prédio (pessoa jurídica)
const BuildingIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="3" width="12" height="18" rx="1" />
    <path d="M16 8h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4" />
    <path d="M8 7h1M12 7h1M8 11h1M12 11h1M8 15h1M12 15h1" />
  </svg>
);

// Check verde reutilizável (validação positiva)
const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Ícone de alerta para erros inline
const AlertIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Mensagem única de telefone inválido (reutilizada em PF e PJ)
const PHONE_INVALID_MSG = 'Telefone inválido. Confira o DDD e digite um celular (9 dígitos) ou fixo válido.';

interface TipoPessoaStepProps {
  selectedType: 'fisica' | 'juridica' | '';
  onSelect: (type: 'fisica' | 'juridica') => void;
  onNext: () => void;
}

const TipoPessoaStep: React.FC<TipoPessoaStepProps> = ({
  selectedType,
  onSelect,
  onNext
}) => {
  const { register, watch, setValue, formState: { errors } } = useFormContext();
  const [showFields, setShowFields] = useState(false);

  // Mesmo gatilho de handleSelectChange, agora disparado pelos tiles
  const handleTypeSelect = (value: 'fisica' | 'juridica') => {
    onSelect(value);
    setShowFields(true);
  };

  // Observar os valores dos campos para validação
  const watchedValues = watch();

  // Validação simples
  const validateForm = () => {
    if (!selectedType) return false;

    if (!watchedValues.tipoContato) return false;

    if (selectedType === 'fisica') {
      const personalData = watchedValues.personalData || {};
      return personalData.fullName && personalData.cpf && personalData.email && validatePhone(personalData.phone || '');
    } else {
      const companyData = watchedValues.companyData || {};
      return companyData.companyName && companyData.cnpj && companyData.email && validatePhone(companyData.phone || '') && companyData.responsibleName && companyData.responsibleCpf;
    }
  };

  const isFormValid = validateForm();

  // Estados de validação positiva derivados dos watchedValues (sem novo state)
  const personalData = watchedValues.personalData || {};
  const companyData = watchedValues.companyData || {};

  const isCpfValid = (personalData.cpf || '').length === 14;
  const isPfEmailValid = EMAIL_RE.test(personalData.email || '');
  const isPfPhoneValid = validatePhone(personalData.phone || '');

  const isCnpjValid = (companyData.cnpj || '').length === 18;
  const isRespCpfValid = (companyData.responsibleCpf || '').length === 14;
  const isPjEmailValid = EMAIL_RE.test(companyData.email || '');
  const isPjPhoneValid = validatePhone(companyData.phone || '');

  // Erros inline de telefone: só aparecem quando há algo digitado e é inválido,
  // para não "gritar" com o campo ainda vazio.
  const pfPhoneErr = !!(personalData.phone || (errors as any).personalData?.phone) && !isPfPhoneValid && (personalData.phone || '').length > 0;
  const pjPhoneErr = !!(companyData.phone || (errors as any).companyData?.phone) && !isPjPhoneValid && (companyData.phone || '').length > 0;

  // Tipo de Contato (obrigatório para PF e PJ). Renderizado como último campo
  // do bloco de dados para manter o mesmo ritmo dos demais campos.
  const tipoContatoField = (
    <ZeplinFieldGroup>
      <ZeplinLabel>Tipo de Contato</ZeplinLabel>
      <ZeplinSelect
        defaultValue=""
        {...register('tipoContato', { required: 'Tipo de contato é obrigatório' })}
      >
        <option value="">Selecione o segmento</option>
        {TIPOS_CONTATO.map((tipo) => (
          <option key={tipo} value={tipo}>{tipo}</option>
        ))}
      </ZeplinSelect>
    </ZeplinFieldGroup>
  );

  return (
    <>
      <ZeplinTitle>Faça seu cadastro!</ZeplinTitle>
      <ZeplinDataSubtitle>Seus dados</ZeplinDataSubtitle>

      <ZeplinStepIndicator>
        <span className="eyebrow">Passo 1 de 2</span>
        <div className="rail">
          <div className="dot current">1</div>
          <div className="line"><div className="fill" /></div>
          <div className="dot future">2</div>
        </div>
        <div className="labels">
          <span className="on">Dados</span>
          <span>Endereço</span>
        </div>
      </ZeplinStepIndicator>

      <ZeplinFieldGroup>
        <ZeplinLabel>Tipo de Pessoa</ZeplinLabel>
        <ZeplinTileGroup role="radiogroup" aria-label="Tipo de Pessoa">
          <ZeplinTile className={selectedType === 'fisica' ? 'selected' : ''}>
            <input
              type="radio"
              name="personType"
              value="fisica"
              checked={selectedType === 'fisica'}
              onChange={() => handleTypeSelect('fisica')}
            />
            <span className="tile-icon"><UserIcon /></span>
            <span className="tile-title">Pessoa Física</span>
            <span className="tile-sub">CPF</span>
            <span className="tile-check"><CheckIcon /></span>
          </ZeplinTile>

          <ZeplinTile className={selectedType === 'juridica' ? 'selected' : ''}>
            <input
              type="radio"
              name="personType"
              value="juridica"
              checked={selectedType === 'juridica'}
              onChange={() => handleTypeSelect('juridica')}
            />
            <span className="tile-icon"><BuildingIcon /></span>
            <span className="tile-title">Pessoa Jurídica</span>
            <span className="tile-sub">CNPJ</span>
            <span className="tile-check"><CheckIcon /></span>
          </ZeplinTile>
        </ZeplinTileGroup>
      </ZeplinFieldGroup>

      {/* Campos para Pessoa Física */}
      {selectedType === 'fisica' && showFields && (
        <ZeplinFieldsReveal>
          <ZeplinFieldGroup>
            <ZeplinLabel>Nome Completo</ZeplinLabel>
            <ZeplinInput
              type="text"
              placeholder="Digite seu nome completo"
              autoComplete="name"
              {...register('personalData.fullName', {
                required: 'Nome completo é obrigatório'
              })}
            />
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>CPF</ZeplinLabel>
            <ZeplinInput
              type="text"
              placeholder="000.000.000-00"
              inputMode="numeric"
              maxLength={14}
              className={isCpfValid ? 'is-valid' : ''}
              {...register('personalData.cpf', {
                required: 'CPF é obrigatório'
              })}
              onChange={(e) => {
                const formatted = formatCPF(e.target.value);
                e.target.value = formatted;
                setValue('personalData.cpf', formatted);
              }}
            />
            {isCpfValid && <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>}
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>
              Data de Nascimento<span className="optional"> (opcional)</span>
            </ZeplinLabel>
            <ZeplinInput
              type="date"
              autoComplete="bday"
              {...register('personalData.birthDate')}
            />
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>E-mail</ZeplinLabel>
            <ZeplinInput
              type="email"
              placeholder="seu@email.com"
              autoComplete="email"
              className={isPfEmailValid ? 'is-valid' : ''}
              {...register('personalData.email', {
                required: 'E-mail é obrigatório'
              })}
            />
            {isPfEmailValid && <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>}
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>Telefone</ZeplinLabel>
            <ZeplinInput
              type="tel"
              placeholder="(11) 99999-9999"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={15}
              className={pfPhoneErr ? 'is-error' : isPfPhoneValid ? 'is-valid' : ''}
              {...register('personalData.phone', {
                required: 'Telefone é obrigatório',
                validate: (value: string) => validatePhone(value || '') || PHONE_INVALID_MSG
              })}
              onChange={(e) => {
                const formatted = formatPhone(e.target.value);
                e.target.value = formatted;
                setValue('personalData.phone', formatted, { shouldValidate: true });
              }}
            />
            {isPfPhoneValid && <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>}
            {pfPhoneErr && (
              <ZeplinError role="alert">
                <AlertIcon /> {PHONE_INVALID_MSG}
              </ZeplinError>
            )}
          </ZeplinFieldGroup>

          {tipoContatoField}
        </ZeplinFieldsReveal>
      )}

      {/* Campos para Pessoa Jurídica */}
      {selectedType === 'juridica' && showFields && (
        <ZeplinFieldsReveal>
          <ZeplinFieldGroup>
            <ZeplinLabel>Razão Social</ZeplinLabel>
            <ZeplinInput
              type="text"
              placeholder="Digite a razão social"
              autoComplete="organization"
              {...register('companyData.companyName', {
                required: 'Razão social é obrigatória'
              })}
            />
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>CNPJ</ZeplinLabel>
            <ZeplinInput
              type="text"
              placeholder="00.000.000/0000-00"
              inputMode="numeric"
              maxLength={18}
              className={isCnpjValid ? 'is-valid' : ''}
              {...register('companyData.cnpj', {
                required: 'CNPJ é obrigatório'
              })}
              onChange={(e) => {
                const formatted = formatCNPJ(e.target.value);
                e.target.value = formatted;
                setValue('companyData.cnpj', formatted);
              }}
            />
            {isCnpjValid && <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>}
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>Nome do Responsável</ZeplinLabel>
            <ZeplinInput
              type="text"
              placeholder="Digite o nome do responsável"
              autoComplete="name"
              {...register('companyData.responsibleName', {
                required: 'Nome do responsável é obrigatório'
              })}
            />
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>CPF do Responsável</ZeplinLabel>
            <ZeplinInput
              type="text"
              placeholder="000.000.000-00"
              inputMode="numeric"
              maxLength={14}
              className={isRespCpfValid ? 'is-valid' : ''}
              {...register('companyData.responsibleCpf', {
                required: 'CPF do responsável é obrigatório'
              })}
              onChange={(e) => {
                const formatted = formatCPF(e.target.value);
                e.target.value = formatted;
                setValue('companyData.responsibleCpf', formatted);
              }}
            />
            {isRespCpfValid && <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>}
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>E-mail</ZeplinLabel>
            <ZeplinInput
              type="email"
              placeholder="contato@empresa.com"
              autoComplete="email"
              className={isPjEmailValid ? 'is-valid' : ''}
              {...register('companyData.email', {
                required: 'E-mail é obrigatório'
              })}
            />
            {isPjEmailValid && <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>}
          </ZeplinFieldGroup>

          <ZeplinFieldGroup>
            <ZeplinLabel>Telefone</ZeplinLabel>
            <ZeplinInput
              type="tel"
              placeholder="(11) 99999-9999"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={15}
              className={pjPhoneErr ? 'is-error' : isPjPhoneValid ? 'is-valid' : ''}
              {...register('companyData.phone', {
                required: 'Telefone é obrigatório',
                validate: (value: string) => validatePhone(value || '') || PHONE_INVALID_MSG
              })}
              onChange={(e) => {
                const formatted = formatPhone(e.target.value);
                e.target.value = formatted;
                setValue('companyData.phone', formatted, { shouldValidate: true });
              }}
            />
            {isPjPhoneValid && <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>}
            {pjPhoneErr && (
              <ZeplinError role="alert">
                <AlertIcon /> {PHONE_INVALID_MSG}
              </ZeplinError>
            )}
          </ZeplinFieldGroup>

          {tipoContatoField}
        </ZeplinFieldsReveal>
      )}

      {selectedType && showFields && (
        <ZeplinButton
          type="button"
          onClick={onNext}
          disabled={!isFormValid}
          className={isFormValid ? 'enabled' : ''}
          style={{ marginTop: 'var(--s-8)' }}
        >
          Continuar
        </ZeplinButton>
      )}
    </>
  );
};

export default TipoPessoaStep;
